import { ssrFetch } from '@/shared/utils/ssrFetch';
import Head from 'next/head';
import Link from 'next/link';
import { sayfaBasligi } from '@/shared/seo/sayfaBasligi';
import { metaAciklama } from '@/shared/seo/metaAciklama';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { BLOG_AUTHOR, authorPersonSchema, authorBylineText } from '@/shared/data/blogAuthor';
import { buildArticleSchema } from '@/shared/schema/articleSchema';
import { buildBreadcrumbSchema } from '@/shared/schema/breadcrumbSchema';
import { buildFaqPageSchema } from '@/shared/schema/faqPageSchema';
import { buildOrganizationMinimal } from '@/shared/schema/organizationSchema';
import { getRelatedToolsWithCta, getHubCtaForBlog } from '@/data/blog-tool-mappings';

/**
 * /blog/[slug] — Blog yazısı (Medium/Substack tarzı, ultra enterprise)
 * =====================================================================
 * - "riskmatik" lockup (lowercase, koyu) sticky top bar
 * - Inter typography (paragraf 19px, H2 28px, H3 22px — Medium hierarchy)
 * - Slate-900 link rengi (indigo değil)
 * - Generous whitespace, 720px article column
 * - Schema.org Article + BreadcrumbList + opsiyonel HowTo/FAQPage
 */

interface FaqItem {
  '@type'?: string;
  name?: string;
  question?: string;
  soru?: string;
  acceptedAnswer?: { '@type'?: string; text: string };
  answer?: string;
  cevap?: string;
}

interface HowToStep {
  '@type': string;
  name: string;
  text: string;
}

interface BlogPost {
  slug: string;
  baslik: string;
  ozet: string;
  icerik: string;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
  ogImage?: string;
  kapakResmi?: string;
  kapakResmiAlt?: string;
  publishedAt: string;
  guncellenmeTarihi?: string;
  kategori?: { ad: string; slug: string };
  tagler?: Array<{ tag: { id: string; ad: string; slug: string } }>;
  okumaSuresi?: number;
  goruntulenme?: number;
  canonicalUrl?: string;
  faqItems?: FaqItem[];
  ekSss?: Array<{ soru: string; cevap: string }>;
  sss?: Array<{ soru: string; cevap: string }>;
  howToSteps?: HowToStep[];
  yapisalVeriTipi?: string;
}

interface RelatedPost {
  id: string;
  slug: string;
  baslik: string;
  ozet: string;
  publishedAt: string;
  kategori?: { ad: string; slug: string };
  okumaSuresi?: number;
}

interface BlogPostPageProps {
  post?: BlogPost;
  related?: RelatedPost[];
}

function stripHtml(html: string): string {
  return String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractHowToSteps(html: string): HowToStep[] {
  if (!html) return [];
  const steps: HowToStep[] = [];
  const regex = /<h2[^>]*>([\s\S]*?)<\/h2>\s*(?:<[^>]+>\s*)*?<p[^>]*>([\s\S]*?)<\/p>/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    const name = stripHtml(match[1]);
    const text = stripHtml(match[2]);
    if (name && text) steps.push({ '@type': 'HowToStep', name, text });
  }
  return steps;
}

function extractFaqItems(html: string): FaqItem[] {
  if (!html) return [];
  const items: FaqItem[] = [];
  const regex = /<h([23])[^>]*>([\s\S]*?)<\/h\1>\s*(?:<[^>]+>\s*)*?<p[^>]*>([\s\S]*?)<\/p>/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    const question = stripHtml(match[2]);
    const answer = stripHtml(match[3]);
    if (question && answer) {
      items.push({ soru: question, cevap: answer });
    }
  }
  return items;
}

/**
 * FAQPage schema'daki soruların sayfada GÖRÜNÜR olması zorunlu (Google yapısal veri kuralı). 2026-09-30
 * denetimi: 20 yazıda faqItems (schema) içerikte hiç görünmüyordu. İçerikte zaten başlık olarak geçen
 * sorular tekrar basılmaz; kalanlar makale sonunda "Sıkça Sorulan Sorular" olarak görünür render edilir.
 */
function buildEkSss(post: BlogPost): Array<{ soru: string; cevap: string }> {
  const norm = (x: string) => stripHtml(x).toLocaleLowerCase('tr-TR').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const govde = norm(post.icerik);
  return (post.sss || []).filter((q) => q.soru && q.cevap && !govde.includes(norm(q.soru)));
}

function BlogPostPage({ post, related }: BlogPostPageProps) {
  const router = useRouter();
  const faqSchema = buildFaqPageSchema(post.sss || []);

  // İlgili Riskmatik araçları — matematiksel Jaccard skoru + özgün CTA (SSoT: data/blog-tool-mappings).
  // Build-time hesaplanır (SSG), runtime maliyet yok.
  const relatedToolsWithCta = getRelatedToolsWithCta(post?.slug);
  const relatedTools = relatedToolsWithCta.map((m) => m.tool);
  const hubCta = getHubCtaForBlog(post?.slug);

  useEffect(() => {
    if (!post?.slug || typeof window === 'undefined') return;
    const key = `riskmatik-view-${post.slug}`;
    const WINDOW_MS = 30 * 60 * 1000;
    const now = Date.now();
    try {
      const last = parseInt(window.sessionStorage.getItem(key) || '0', 10);
      if (now - last < WINDOW_MS) return;
      window.sessionStorage.setItem(key, String(now));
    } catch {}
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
    const url = `${apiUrl}/api/blog/posts/${post.slug}/view`;
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url);
    } else {
      fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
    }
  }, [post?.slug]);

  if (router.isFallback) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-slate-500 text-sm">Yükleniyor…</p>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Yazı Bulunamadı</h1>{' '}
          <Link href="/blog" className="text-slate-900 underline decoration-slate-300 hover:decoration-slate-900 font-medium">
            ← Tüm yazılara dön
          </Link>
        </div>
      </main>
    );
  }

  const canonicalUrl = post.canonicalUrl || `https://www.riskmatik.com/blog/${post.slug}`;
  const seoTitle = sayfaBasligi(post.seoTitle || `${post.baslik} | Riskmatik`);
  // Açıklama veritabanından gelir → sayfa şablonlarıyla aynı ≤160 kuralı (metaAciklama SSoT)
  const seoDescription = metaAciklama(post.seoDescription || post.ozet || '');
  const ogImage = post.ogImage || post.kapakResmi || 'https://www.riskmatik.com/og-image-v4.png';

  // Schema.org @type seçimi — AI / görüntü işleme / teknik konulu yazılar
  // 'TechArticle' (semantik authority sinyali), diğerleri 'Article'.
  // Karar SEO keyword'lerinden türetilir — admin paneli SSoT, drift yok.
  const isTechArticle = Array.isArray(post.seoKeywords)
    && post.seoKeywords.some((k) => /yapay zeka|\bai\b|görüntü işleme|fotoğraf analiz|gemini|machine learning/i.test(k || ''));
  const articleType = isTechArticle ? 'TechArticle' : 'Article';

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {post.seoKeywords?.length > 0 && <meta name="keywords" content={post.seoKeywords.join(', ')} />}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Riskmatik" />
        <meta property="og:locale" content="tr_TR" />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:modified_time" content={post.guncellenmeTarihi} />
        {post.kategori && <meta property="article:section" content={post.kategori.ad} />}
        {post.tagler?.map((t) => (
          <meta key={t.tag.id} property="article:tag" content={t.tag.ad} />
        ))}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildArticleSchema({
              headline: post.baslik,
              description: post.ozet,
              image: [ogImage],
              datePublished: post.publishedAt,
              dateModified: post.guncellenmeTarihi,
              author: authorPersonSchema(),
              publisher: {
                ...buildOrganizationMinimal(),
                logo: { '@type': 'ImageObject', url: 'https://www.riskmatik.com/images/riskmatik-logo.svg' }
              },
              mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
              articleSection: post.kategori?.ad,
              keywords: post.seoKeywords?.join(', ')
            }, articleType))
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildBreadcrumbSchema([
              { name: 'Ana Sayfa', url: 'https://www.riskmatik.com' },
              { name: 'Blog', url: 'https://www.riskmatik.com/blog' },
              ...(post.kategori
                ? [{ name: post.kategori.ad, url: `https://www.riskmatik.com/blog?kategori=${post.kategori.slug}` }]
                : []),
              { name: post.baslik, url: canonicalUrl }
            ]))
          }}
        />

        {/* HowTo schema KALDIRILDI (2026-08-10): Google HowTo rich-result'ı Ağu 2023'te kaldırdı → ölü ağırlık.
            post.yapisalVeriTipi/howToSteps DB'de kalır (zararsız), JSON-LD basılmaz. */}
        {/* FAQPage schema — faqItems DOLU İSE her zaman emit edilir (yapisalVeriTipi'nden
            BAĞIMSIZ). Bir yazı hem HowTo hem FAQPage olabilir (Google çoklu schema destekler);
            eski koşul HowTo-tipi + faqItems olan yazıları FAQPage'den dışlıyordu (2026-06-11 fix).
            DB'deki farklı faqItems formatları getStaticProps'ta post.sss'e normalize edilir. */}
        {faqSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        )}
      </Head>
      <main>

      <div className="min-h-screen bg-white">
        {/* Sticky lockup bar — riskmatik lowercase */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200">
          <div className="max-w-[760px] mx-auto px-5 sm:px-6 h-14 flex items-center justify-between">
            <Link href="/" className="inline-flex items-center gap-2 group">
              <span className="text-[19px] font-black tracking-[-0.04em] lowercase text-slate-900 group-hover:text-slate-700 transition">
                riskmatik
              </span>{' '}
              <span className="hidden sm:inline-flex items-center text-[10px] font-bold text-slate-500 uppercase tracking-[0.16em]">
                Blog
              </span>
            </Link>{' '}
            <Link
              href="/blog"
              className="text-sm font-medium text-slate-700 hover:text-slate-900 underline decoration-transparent hover:decoration-slate-900 underline-offset-4 transition"
            >
              Tüm yazılar
            </Link>
          </div>
        </header>{' '}

        {/* Breadcrumb mini */}
        <nav className="max-w-[720px] mx-auto px-5 sm:px-6 pt-10 text-[12px] text-slate-500">
          <Link href="/blog" className="hover:text-slate-900 transition">Blog</Link>
          {post.kategori && (
            <>
              <span className="mx-2 text-slate-300">·</span><span className="sr-only"> </span>
              <Link
                href={`/blog?kategori=${post.kategori.slug}`}
                className="hover:text-slate-900 transition uppercase tracking-[0.12em] text-[11px] font-semibold"
              >
                {post.kategori.ad}
              </Link>
            </>
          )}
        </nav>{' '}

        {/* Article header */}
        <header className="max-w-[720px] mx-auto px-5 sm:px-6 pt-6 pb-10">
          <h1
            className="text-[38px] md:text-[46px] font-extrabold text-slate-900 leading-[1.12] tracking-[-0.025em] mb-5"
            style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}
          >
            {post.baslik}
          </h1>{' '}

          <p className="text-[20px] md:text-[22px] text-slate-600 leading-[1.5] mb-8 font-normal">
            {post.ozet}
          </p>{' '}

          <div className="flex items-center gap-3 pt-6 border-t border-slate-200">
            <div className="w-9 h-9 rounded-full bg-slate-900 flex items-center justify-center text-white text-[13px] font-bold tabular-nums">
              R
            </div>{' '}
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-slate-900">Riskmatik</div>{' '}
              <div className="text-[12px] text-slate-500 mt-0.5">
                <time dateTime={post.publishedAt}>
                  {new Date(post.publishedAt).toLocaleDateString('tr-TR', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </time><span className="sr-only"> </span>
                {post.okumaSuresi && <span className="mx-1.5">·</span>}<span className="sr-only"> </span>
                {post.okumaSuresi && <span>{post.okumaSuresi} dk okuma</span>}<span className="sr-only"> </span>
                <span className="mx-1.5">·</span><span className="sr-only"> </span>
                <span className="tabular-nums">{(post.goruntulenme || 0).toLocaleString('tr-TR')} görüntülenme</span>
              </div>
            </div>
          </div>
        </header>{' '}

        {/* Cover */}
        {post.kapakResmi && (
          <figure className="max-w-[860px] mx-auto px-5 sm:px-6 mb-12">
            <div className="relative aspect-[16/9] overflow-hidden rounded-lg bg-slate-100 border border-slate-200">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={post.kapakResmi} alt={post.kapakResmiAlt || post.baslik} className="w-full h-full object-cover" loading="eager" />
            </div>{' '}
            {post.kapakResmiAlt && (
              <figcaption className="text-center text-[13px] text-slate-500 mt-3 italic">
                {post.kapakResmiAlt}
              </figcaption>
            )}
          {' '}</figure>
        )}

        {/* Hub CTA — makale içeriği başlamadan önce, intent match için */}
        {hubCta && (
          <div className="max-w-[720px] mx-auto px-5 sm:px-6 mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-1">Hazır Belgeler</p>{' '}
                <p className="text-[15px] font-semibold text-slate-900 leading-snug mb-1">{hubCta.label}</p>{' '}
                <p className="text-[13px] text-slate-600 leading-relaxed">{hubCta.description}</p>
              </div>{' '}
              <Link
                href={hubCta.href}
                className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] bg-slate-900 text-white text-[13px] font-semibold rounded-lg hover:bg-slate-800 transition whitespace-nowrap"
              >
                İncele
              </Link>
            </div>
          </div>
        )}{' '}

        {/* Article body */}
        <article className="max-w-[720px] mx-auto px-5 sm:px-6 pb-16">
          <div
            className="blog-content text-slate-900"
            style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}
            dangerouslySetInnerHTML={{ __html: post.icerik }}
          />{' '}

          {post.ekSss?.length > 0 && (
            <section className="mt-12 border-t border-slate-200 pt-10" aria-labelledby="blog-sss">
              <h2 id="blog-sss" className="text-2xl font-bold text-slate-900 tracking-tight mb-6">Sıkça Sorulan Sorular</h2>{' '}
              {post.ekSss.map((q) => (
                <div key={q.soru} className="mb-7">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{q.soru}</h3>{' '}
                  <p className="text-[17px] leading-relaxed text-slate-700">{q.cevap}</p>
                {' '}</div>
              ))}
            {' '}</section>
          )}

          <style jsx>{`
            .blog-content :global(p) {
              font-size: 19px;
              line-height: 1.78;
              color: #1f2937;
              margin: 0 0 1.6em 0;
              letter-spacing: -0.003em;
            }
            .blog-content :global(h2) {
              font-size: 28px;
              font-weight: 700;
              color: #0f172a;
              margin: 2.4em 0 0.7em 0;
              line-height: 1.25;
              letter-spacing: -0.018em;
            }
            .blog-content :global(h3) {
              font-size: 22px;
              font-weight: 700;
              color: #0f172a;
              margin: 1.9em 0 0.5em 0;
              line-height: 1.3;
              letter-spacing: -0.014em;
            }
            .blog-content :global(h4) {
              font-size: 18px;
              font-weight: 700;
              color: #0f172a;
              margin: 1.5em 0 0.4em 0;
            }
            .blog-content :global(ul),
            .blog-content :global(ol) {
              margin: 0 0 1.6em 0;
              padding-left: 1.6em;
            }
            .blog-content :global(li) {
              font-size: 19px;
              line-height: 1.72;
              color: #1f2937;
              margin-bottom: 0.5em;
            }
            .blog-content :global(a) {
              color: #0f172a;
              text-decoration: underline;
              text-decoration-color: rgb(203 213 225);
              text-underline-offset: 4px;
              text-decoration-thickness: 1.5px;
              transition: text-decoration-color 0.15s ease;
            }
            .blog-content :global(a:hover) {
              text-decoration-color: #0f172a;
            }
            .blog-content :global(strong) {
              color: #0f172a;
              font-weight: 700;
            }
            .blog-content :global(em) {
              font-style: italic;
              color: #334155;
            }
            .blog-content :global(blockquote) {
              border-left: 3px solid #0f172a;
              padding: 0.4em 0 0.4em 1.4em;
              margin: 1.8em 0;
              color: #334155;
              font-style: italic;
              background: transparent;
            }
            .blog-content :global(blockquote p) {
              font-size: 20px;
              line-height: 1.6;
              margin: 0.4em 0;
            }
            .blog-content :global(code) {
              background: #f1f5f9;
              color: #0f172a;
              padding: 2px 6px;
              border-radius: 4px;
              font-size: 0.88em;
              font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
              border: 1px solid #e2e8f0;
            }
            .blog-content :global(pre) {
              background: #0f172a;
              color: #f8fafc;
              padding: 1.2em;
              border-radius: 8px;
              overflow-x: auto;
              margin: 1.6em 0;
              font-size: 14px;
            }
            .blog-content :global(pre code) {
              background: transparent;
              color: inherit;
              padding: 0;
              border: none;
            }
            .blog-content :global(hr) {
              border: none;
              border-top: 1px solid #e2e8f0;
              margin: 3em auto;
              width: 100%;
            }
            .blog-content :global(img) {
              max-width: 100%;
              border-radius: 8px;
              margin: 2em 0;
              border: 1px solid #e2e8f0;
            }
            .blog-content :global(table) {
              display: block;
              width: 100%;
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
              border-collapse: collapse;
              margin: 1.8em 0;
              font-size: 16px;
            }
            .blog-content :global(th),
            .blog-content :global(td) {
              border: 1px solid #e2e8f0;
              padding: 0.6em 0.8em;
              text-align: left;
            }
            .blog-content :global(th) {
              background: #f8fafc;
              font-weight: 700;
              color: #0f172a;
            }
          `}</style>

          {post.tagler?.length > 0 && (
            <div className="mt-14 pt-8 border-t border-slate-200">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.16em] mb-3">Etiketler</div>{' '}
              <div className="flex flex-wrap gap-2">
                {post.tagler.map((t) => (
                  <Link
                    key={t.tag.id}
                    href={`/blog?tag=${t.tag.slug}`}
                    className="inline-block px-3 py-1.5 text-[13px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-full hover:bg-slate-100 hover:border-slate-300 transition"
                  >
                    {t.tag.ad}
                  {' '}</Link>
                ))}
              </div>
            </div>
          )}
        </article>{' '}

        {/* İlgili Araçlar — blog → tool internal link akışı (SEO PageRank flow).
            Stratejik karar: "İlgili Yazılar"dan ÖNCE çünkü action-oriented
            engagement passive content consumption'dan daha değerli — okuyucu
            makaleyi okuyup somut bir araçla devam eder. */}
        {relatedTools.length > 0 && (
          <section className="border-t border-slate-200 bg-white">
            <div className="max-w-[720px] mx-auto px-5 sm:px-6 py-14">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.18em] mb-6">
                İlgili Araçlar
              </div>{' '}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedTools.map((tool) => (
                  <Link
                    key={tool.slug}
                    href={tool.href}
                    className="group flex flex-col p-4 bg-slate-50 border border-slate-200 hover:border-slate-400 hover:bg-white hover:shadow-sm rounded-lg transition focus:outline-none focus:ring-2 focus:ring-slate-900"
                  >
                    <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-1.5">
                      {tool.eyebrow}
                    </div>{' '}
                    <h3 className="text-base font-bold text-slate-900 mb-1.5 group-hover:text-slate-700">
                      {tool.title}
                    </h3>{' '}
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
                      {tool.description}
                    </p>{' '}
                    <span className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-slate-900 group-hover:text-slate-700">
                      Aracı kullan
                      <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                        <path d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                      </svg>
                    </span>
                  {' '}</Link>
                ))}
              </div>
            </div>
          {' '}</section>
        )}

        {/* Related — Medium tarzı sade liste */}
        {related?.length > 0 && (
          <section className="border-t border-slate-200 bg-slate-50/40">
            <div className="max-w-[720px] mx-auto px-5 sm:px-6 py-14">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.18em] mb-6">
                İlgili Yazılar
              </div>{' '}
              <div className="space-y-7">
                {related.map((r) => (
                  <article key={r.id} className="group">
                    <Link href={`/blog/${r.slug}`} className="block">
                      {r.kategori && (
                        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.14em] mb-1.5">
                          {r.kategori.ad}
                        </div>
                      )}{' '}
                      <h3 className="text-[20px] font-bold text-slate-900 leading-snug mb-1.5 group-hover:underline decoration-slate-900 underline-offset-4 decoration-1">
                        {r.baslik}
                      </h3>{' '}
                      <p className="text-[15px] text-slate-600 leading-relaxed line-clamp-2">{r.ozet}</p>{' '}
                      <div className="text-[12px] text-slate-500 mt-2">
                        <time dateTime={r.publishedAt}>
                          {new Date(r.publishedAt).toLocaleDateString('tr-TR', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })}
                        </time>
                        {r.okumaSuresi && <> · {r.okumaSuresi} dk okuma</>}
                      </div>
                    </Link>
                  {' '}</article>
                ))}
              </div>
            </div>
          </section>
        )}{' '}

        {/* Footer back link */}
        <div className="border-t border-slate-200 bg-white">
          <div className="max-w-[720px] mx-auto px-5 sm:px-6 py-10 flex items-center justify-between text-[14px]">
            <Link href="/blog" className="text-slate-700 hover:text-slate-900 underline decoration-slate-300 hover:decoration-slate-900 underline-offset-4 transition font-medium">
              ← Tüm yazılar
            </Link>{' '}
            <Link href="/" className="text-[13px] font-black tracking-[-0.04em] lowercase text-slate-900 hover:text-slate-700 transition">
              riskmatik
            </Link>
          </div>
        </div>
      </div>
      </main>
    </>
  );
}

BlogPostPage.layout = null;

export default BlogPostPage;

export async function getStaticPaths() {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
    const res = await ssrFetch(`${apiUrl}/api/blog/sitemap`);
    if (!res.ok) return { paths: [], fallback: 'blocking' };
    const data: { slugs: Array<{ slug: string }> } = await res.json();
    const paths = (data.slugs || []).map((s) => ({ params: { slug: s.slug } }));
    return { paths, fallback: 'blocking' };
  } catch {
    return { paths: [], fallback: 'blocking' };
  }
}

export async function getStaticProps({ params }: { params: { slug: string } }) {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
    const res = await ssrFetch(`${apiUrl}/api/blog/posts/${params.slug}`);
    if (!res.ok) {
      if (res.status === 404) return { notFound: true, revalidate: 60 };
      throw new Error(`Fetch failed: ${res.status}`);
    }
    const data: { post: BlogPost; related: RelatedPost[] } = await res.json();
    const post = data.post;
    if (post?.icerik && post.yapisalVeriTipi === 'HowTo') {
      post.howToSteps = extractHowToSteps(post.icerik);
    }
    if (post?.icerik && post.yapisalVeriTipi === 'FAQPage') {
      // DB'de manuel faqItems set edilmişse onu kullan (curated content),
      // yoksa içerikten otomatik parse et (legacy fallback).
      if (!Array.isArray(post.faqItems) || post.faqItems.length === 0) {
        post.faqItems = extractFaqItems(post.icerik);
      }
    }
    post.sss = (Array.isArray(post.faqItems) ? post.faqItems : [])
      .map((f) => ({ soru: f.name || f.question || f.soru || '', cevap: f.acceptedAnswer?.text || f.answer || f.cevap || '' }))
      .filter((q) => q.soru && q.cevap);
    post.ekSss = buildEkSss(post);
    return { props: { post, related: data.related || [] }, revalidate: 60 };
  } catch (err) {
    // 5xx/ağ hatası (gerçek 404 try içinde ayrı ele alınır, buraya gelmez): ErrorLog'a yazar; çalışma anında fırlatır →
    // Next.js önceki sağlam yazıyı sunar (eskiden 60 sn 404 → arama motoru yazıyı kaybedebilirdi), derlemede notFound.
    return (await import('@/shared/utils/ssgHatasi')).ssgHatasi(err, 'getStaticProps:blog-slug', { notFound: true, revalidate: 60 }, { pathname: `/blog/${params?.slug || ''}` });
  }
}
